import { useState, useEffect, useCallback } from 'react';
import { reportsAPI, projectsAPI } from '../utils/api';
import { format, subMonths, startOfMonth, endOfMonth, subWeeks, startOfWeek, endOfWeek } from 'date-fns';
import { useToast, apiError } from '../components/Toast';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

// ── small helpers ──────────────────────────────────────────────────────────
const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl p-3 shadow-xl text-xs" style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
      {label && <p className="font-semibold mb-1" style={{ color:'var(--text)' }}>{label}</p>}
      {payload.map((p,i) => <p key={i} style={{ color:p.color }}>{p.name}: <b>{p.value}</b></p>)}
    </div>
  );
};

const StatCard = ({ label, value, sub, color='#2E539F', icon }) => (
  <div className="card px-4 py-3 flex items-start gap-3">
    {icon && <span className="text-2xl flex-shrink-0">{icon}</span>}
    <div>
      <p className="text-2xl font-bold" style={{ color }}>{value}</p>
      <p className="text-sm font-medium" style={{ color:'var(--text)' }}>{label}</p>
      {sub && <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{sub}</p>}
    </div>
  </div>
);

const RAG_DOT = { G:'#10b981', Y:'#f59e0b', R:'#ef4444', null:'#94a3b8', undefined:'#94a3b8' };

const TABS = [
  { key:'weekly',     label:'Weekly Report'      },
  { key:'monthly',    label:'Monthly Trends'     },
  { key:'comparison', label:'Project Comparison' },
  { key:'team',       label:'Team Performance'   },
];

export default function ReportsPage() {
  const toast = useToast();
  const [tab,       setTab]       = useState('weekly');
  const [error, setError] = useState('');
  const [projects,  setProjects]  = useState([]);
  const [projId,    setProjId]    = useState('');
  const [loading,   setLoading]   = useState(false);

  // Weekly state
  const [weekStart, setWeekStart] = useState(format(startOfWeek(new Date(),{weekStartsOn:1}),'yyyy-MM-dd'));
  const [weeklyData,setWeeklyData]= useState(null);

  // Monthly state
  const [monthFrom, setMonthFrom] = useState(format(startOfMonth(subMonths(new Date(),5)),'yyyy-MM-dd'));
  const [monthTo,   setMonthTo]   = useState(format(endOfMonth(new Date()),'yyyy-MM-dd'));
  const [monthData, setMonthData] = useState(null);

  // Comparison state
  const [compData,  setCompData]  = useState([]);

  // Team state
  const [teamData,  setTeamData]  = useState([]);

  useEffect(() => {
    projectsAPI.getAll({ limit: 0 }).then(r => setProjects(r.data||[])).catch(e => toast.error(apiError(e, 'Could not load this page.')));
  }, []);

  const loadWeekly = useCallback(async () => {
    setLoading(true);
    try {
      const end = format(endOfWeek(new Date(weekStart),{weekStartsOn:1}),'yyyy-MM-dd');
      const r = await reportsAPI.getWeekly({ week_start:weekStart, week_end:end, project_id:projId||undefined });
      setWeeklyData(r.data);
    } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, [weekStart, projId]);

  const loadMonthly = useCallback(async () => {
    setLoading(true);
    try {
      const r = await reportsAPI.getMonthly({ from:monthFrom, to:monthTo, project_id:projId||undefined });
      setMonthData(r.data);
    } catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, [monthFrom, monthTo, projId]);

  const loadComparison = useCallback(async () => {
    setLoading(true);
    try { const r = await reportsAPI.getComparison(); setCompData(r.data||[]); }
    catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, []);

  const loadTeam = useCallback(async () => {
    setLoading(true);
    try { const r = await reportsAPI.getTeam({ from:monthFrom, to:monthTo }); setTeamData(r.data||[]); }
    catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); }
    finally { setLoading(false); }
  }, [monthFrom, monthTo]);

  useEffect(() => {
    if (tab==='weekly')     loadWeekly();
    if (tab==='monthly')    loadMonthly();
    if (tab==='comparison') loadComparison();
    if (tab==='team')       loadTeam();
  }, [tab, loadWeekly, loadMonthly, loadComparison, loadTeam]);

  const Spinner = () => <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>Progress, trends and team performance across all projects</p>
        </div>
        <select className="select w-52 text-sm" value={projId} onChange={e=>setProjId(e.target.value)}>
          <option value="">All Projects</option>
          {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* Tab bar */}
      <div className="border-b flex gap-1 overflow-x-auto" style={{ borderColor:'var(--border)' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors
              ${tab===t.key?'border-brand-500 text-brand-500':'border-transparent'}`}
            style={tab!==t.key?{color:'var(--text-secondary)'}:{}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── WEEKLY TAB ─────────────────────────────────────────────────── */}
      {tab==='weekly' && (
        <div className="space-y-5">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm" style={{ color:'var(--text-secondary)' }}>Week of:</label>
              <input type="date" className="input text-sm w-44" value={weekStart} onChange={e=>setWeekStart(e.target.value)}/>
            </div>
            <button onClick={()=>setWeekStart(format(startOfWeek(subWeeks(new Date(weekStart),1),{weekStartsOn:1}),'yyyy-MM-dd'))} className="btn-secondary text-xs px-3">← Prev</button>
            <button onClick={()=>setWeekStart(format(startOfWeek(new Date(),{weekStartsOn:1}),'yyyy-MM-dd'))} className="btn-secondary text-xs px-3">This Week</button>
            <button onClick={()=>setWeekStart(format(startOfWeek(new Date(new Date(weekStart).getTime()+7*86400000),{weekStartsOn:1}),'yyyy-MM-dd'))} className="btn-secondary text-xs px-3">Next →</button>
          </div>

          {loading ? <Spinner/> : weeklyData && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatCard icon="✅" label="Tasks Completed"   value={weeklyData.summary.tasks_completed}    color="#10b981"/>
                <StatCard icon="🐛" label="Bugs Resolved"     value={weeklyData.summary.bugs_resolved}       color="#2E539F"/>
                <StatCard icon="🎓" label="Trainings"         value={weeklyData.summary.trainings_delivered} color="#8b5cf6"/>
                <StatCard icon="⏳" label="Pending Items"     value={weeklyData.summary.pending_items}       color="#f59e0b"/>
                <StatCard icon="🚫" label="Blockers"          value={weeklyData.summary.blockers}            color="#ef4444"/>
                <StatCard icon="📋" label="Open CRs"          value={weeklyData.summary.open_crs}            color="#8b5cf6"/>
              </div>

              {weeklyData.tasks_completed.length > 0 && (
                <div className="card">
                  <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Completed Tasks ({weeklyData.tasks_completed.length})</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b" style={{ borderColor:'var(--border)' }}>
                        {['Task','Project','Priority','Assigned To'].map(h=><th key={h} className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{h}</th>)}
                      </tr></thead>
                      <tbody>{weeklyData.tasks_completed.map(t=>(
                        <tr key={t.id} className="border-b last:border-0 hover:bg-[var(--surface-alt)]" style={{ borderColor:'var(--border)' }}>
                          <td className="py-2.5 px-3 font-medium" style={{ color:'var(--text)' }}>{t.title}</td>
                          <td className="py-2.5 px-3 text-xs" style={{ color:'var(--text-secondary)' }}>{t.project_name}</td>
                          <td className="py-2.5 px-3"><span className="text-xs px-2 py-0.5 rounded-full" style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>{t.priority}</span></td>
                          <td className="py-2.5 px-3 text-xs" style={{ color:'var(--text-secondary)' }}>{t.assigned_to_name||'—'}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              )}

              {weeklyData.blockers.length > 0 && (
                <div className="card" style={{ border:'1px solid #fca5a5' }}>
                  <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color:'#ef4444' }}>🚫 Blockers ({weeklyData.blockers.length})</h3>
                  {weeklyData.blockers.map(b=>(
                    <div key={b.id} className="flex items-center gap-2 py-2 border-b last:border-0" style={{ borderColor:'var(--border)' }}>
                      <span style={{ color:'var(--text)' }}>{b.title}</span>
                      <span className="text-xs ml-auto" style={{ color:'var(--text-secondary)' }}>{b.project_name}</span>
                    </div>
                  ))}
                </div>
              )}

              {weeklyData.open_crs.length > 0 && (
                <div className="card">
                  <h3 className="font-semibold text-sm mb-3" style={{ color:'var(--text)' }}>Open Change Requests</h3>
                  {weeklyData.open_crs.map(c=>(
                    <div key={c.id} className="flex items-center gap-2 py-2 border-b last:border-0" style={{ borderColor:'var(--border)' }}>
                      <span className="text-sm" style={{ color:'var(--text)' }}>{c.title}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full ml-auto" style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>{c.approval_status}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── MONTHLY TAB ─────────────────────────────────────────────────── */}
      {tab==='monthly' && (
        <div className="space-y-5">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm" style={{ color:'var(--text-secondary)' }}>From:</label>
              <input type="date" className="input text-sm w-40" value={monthFrom} onChange={e=>setMonthFrom(e.target.value)}/>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm" style={{ color:'var(--text-secondary)' }}>To:</label>
              <input type="date" className="input text-sm w-40" value={monthTo} onChange={e=>setMonthTo(e.target.value)}/>
            </div>
            {['3M','6M','12M'].map(p => {
              const months = parseInt(p);
              return (
                <button key={p} onClick={()=>{ setMonthFrom(format(startOfMonth(subMonths(new Date(),months-1)),'yyyy-MM-dd')); setMonthTo(format(endOfMonth(new Date()),'yyyy-MM-dd')); }}
                  className="btn-secondary text-xs px-3">{p}</button>
              );
            })}
          </div>

          {loading ? <Spinner/> : monthData && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="card">
                  <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Tasks Completed per Month</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={monthData.by_month.tasks} margin={{top:5,right:10,bottom:5,left:0}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4}/>
                      <XAxis dataKey="month" tick={{fontSize:11,fill:'var(--text-secondary)'}} tickLine={false}/>
                      <YAxis tick={{fontSize:11,fill:'var(--text-secondary)'}} tickLine={false} axisLine={false}/>
                      <Tooltip content={<Tip/>}/>
                      <Legend wrapperStyle={{fontSize:12}}/>
                      <Bar dataKey="completed" name="Completed" fill="#10b981" radius={[4,4,0,0]}/>
                      <Bar dataKey="total"     name="Total"     fill="#e2e8f0" radius={[4,4,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="card">
                  <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Bug Trend (Opened vs Resolved)</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={monthData.by_month.bugs} margin={{top:5,right:10,bottom:5,left:0}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4}/>
                      <XAxis dataKey="month" tick={{fontSize:11,fill:'var(--text-secondary)'}} tickLine={false}/>
                      <YAxis tick={{fontSize:11,fill:'var(--text-secondary)'}} tickLine={false} axisLine={false}/>
                      <Tooltip content={<Tip/>}/>
                      <Legend wrapperStyle={{fontSize:12}}/>
                      <Line type="monotone" dataKey="opened"   name="Opened"   stroke="#ef4444" strokeWidth={2} dot={false}/>
                      <Line type="monotone" dataKey="resolved" name="Resolved" stroke="#10b981" strokeWidth={2} dot={false}/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="card">
                  <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Change Requests per Month</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={monthData.by_month.crs} margin={{top:5,right:10,bottom:5,left:0}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4}/>
                      <XAxis dataKey="month" tick={{fontSize:11,fill:'var(--text-secondary)'}} tickLine={false}/>
                      <YAxis tick={{fontSize:11,fill:'var(--text-secondary)'}} tickLine={false} axisLine={false}/>
                      <Tooltip content={<Tip/>}/>
                      <Legend wrapperStyle={{fontSize:12}}/>
                      <Bar dataKey="submitted"    name="Submitted"    fill="#8b5cf6" radius={[4,4,0,0]}/>
                      <Bar dataKey="implemented"  name="Implemented"  fill="#5CC3BC" radius={[4,4,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="card">
                  <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Period Summary</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <StatCard icon="✅" label="Tasks Done"   value={monthData.summary?.tasks_done||0}  sub={`of ${monthData.summary?.tasks_total||0} total`} color="#10b981"/>
                    <StatCard icon="📊" label="Effort Ratio" value={monthData.summary?.effort_ratio ? `${(monthData.summary.effort_ratio*100).toFixed(0)}%` : '—'} sub="actual vs estimated" color="#2E539F"/>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── COMPARISON TAB ──────────────────────────────────────────────── */}
      {tab==='comparison' && (
        <div className="space-y-4">
          {loading ? <Spinner/> : compData.length === 0 ? (
            <div className="card text-center py-16"><p className="text-3xl mb-2">📊</p><p style={{ color:'var(--text-secondary)' }}>No active projects to compare</p></div>
          ) : (
            <>
              <div className="card overflow-hidden p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                        {['Project','Health','Priority','Progress','Tasks','Open Bugs','Critical','Pending CRs'].map(h=>(
                          <th key={h} className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {compData.map(p => (
                        <tr key={p.id} className="border-b last:border-0 hover:bg-[var(--surface-alt)] transition-colors" style={{ borderColor:'var(--border)' }}>
                          <td className="py-3 px-4 font-semibold" style={{ color:'var(--text)' }}>{p.name}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1.5">
                              {[p.health_schedule,p.health_budget,p.health_scope].map((h,i) => (
                                <div key={i} className="w-3 h-3 rounded-full" style={{ background:RAG_DOT[h]||'#94a3b8' }} title={['Schedule','Budget','Scope'][i]+': '+h}/>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 px-4"><span className="text-xs px-2 py-0.5 rounded-full" style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>{p.priority}</span></td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-2 rounded-full overflow-hidden" style={{ background:'var(--border)' }}>
                                <div className="h-full rounded-full" style={{ width:`${p.progress||0}%`, background:'#10b981' }}/>
                              </div>
                              <span className="text-xs font-semibold" style={{ color:'var(--text)' }}>{p.progress||0}%</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">{p.tasks_done}/{p.tasks_total}</td>
                          <td className="py-3 px-4 text-center" style={{ color:p.bugs_open>0?'#f97316':'var(--text-secondary)' }}>{p.bugs_open}</td>
                          <td className="py-3 px-4 text-center" style={{ color:p.critical_bugs>0?'#ef4444':'var(--text-secondary)' }}>{p.critical_bugs}</td>
                          <td className="py-3 px-4 text-center" style={{ color:p.pending_crs>0?'#8b5cf6':'var(--text-secondary)' }}>{p.pending_crs}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Progress bar chart */}
              <div className="card">
                <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Completion by Project</h3>
                <ResponsiveContainer width="100%" height={Math.max(180, compData.length*40)}>
                  <BarChart data={compData} layout="vertical" margin={{top:5,right:60,bottom:5,left:0}}>
                    <XAxis type="number" domain={[0,100]} tick={{fontSize:11,fill:'var(--text-secondary)'}} tickLine={false} tickFormatter={v=>`${v}%`}/>
                    <YAxis type="category" dataKey="name" tick={{fontSize:11,fill:'var(--text)'}} tickLine={false} axisLine={false} width={120}/>
                    <Tooltip content={<Tip/>} formatter={(v)=>`${v}%`}/>
                    <Bar dataKey="progress" name="Progress %" fill="#2E539F" radius={[0,4,4,0]} label={{position:'right',formatter:v=>`${v}%`,fill:'var(--text-secondary)',fontSize:11}}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── TEAM TAB ─────────────────────────────────────────────────────── */}
      {tab==='team' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm" style={{ color:'var(--text-secondary)' }}>
              Period:
              <input type="date" className="input text-sm w-40" value={monthFrom} onChange={e=>setMonthFrom(e.target.value)}/>
              <span>to</span>
              <input type="date" className="input text-sm w-40" value={monthTo} onChange={e=>setMonthTo(e.target.value)}/>
            </div>
          </div>
          {loading ? <Spinner/> : teamData.length === 0 ? (
            <div className="card text-center py-12"><p className="text-3xl mb-2">👥</p><p style={{ color:'var(--text-secondary)' }}>No team data available</p></div>
          ) : (
            <div className="card overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                      {['Member','Role','Tasks Done','Activities Done','Actual Hours','Est. Hours','Efficiency'].map(h=>(
                        <th key={h} className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {teamData.map(m => {
                      const eff = m.estimated_hours > 0 ? Math.round((m.actual_hours/m.estimated_hours)*100) : null;
                      return (
                        <tr key={m.id} className="border-b last:border-0 hover:bg-[var(--surface-alt)] transition-colors" style={{ borderColor:'var(--border)' }}>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                                style={{ background:m.avatar_color||'#2E539F' }}>
                                {m.name[0].toUpperCase()}
                              </div>
                              <span className="font-semibold" style={{ color:'var(--text)' }}>{m.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-xs" style={{ color:'var(--text-secondary)' }}>{m.role}</td>
                          <td className="py-3 px-4 text-center font-semibold" style={{ color:'#10b981' }}>{m.tasks_completed}<span className="text-xs font-normal" style={{ color:'var(--text-secondary)' }}>/{m.tasks_assigned}</span></td>
                          <td className="py-3 px-4 text-center font-semibold" style={{ color:'#2E539F' }}>{m.activities_done}<span className="text-xs font-normal" style={{ color:'var(--text-secondary)' }}>/{m.activities_total}</span></td>
                          <td className="py-3 px-4 text-center">{parseFloat(m.actual_hours||0).toFixed(1)}h</td>
                          <td className="py-3 px-4 text-center">{parseFloat(m.estimated_hours||0).toFixed(1)}h</td>
                          <td className="py-3 px-4 text-center">
                            {eff !== null ? (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                style={{ background:eff<=110?'#ecfdf5':'#fef3c7', color:eff<=110?'#059669':'#d97706' }}>
                                {eff}%
                              </span>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
